import type { AttachedFile } from "@/app/api/types/email";

/**
 * STRICT filename match between a source-document URL and an attachment name:
 * the URL's last path segment (query/hash stripped, so MinIO presigned URLs
 * still resolve; then percent-decoded so an encoded segment like
 * `bon%20commande.xlsx` matches the raw attachment name `bon commande.xlsx`)
 * must EQUAL the attachment filename. There is deliberately NO
 * substring fallback: a loose `docUrl.includes(name)` match could resolve a
 * source doc to a DIFFERENT attachment whose name is merely a substring of the
 * source URL (e.g. source `report-order.xlsx`, attachment `order.xlsx`), which
 * would auto-open the wrong workbook AND overlay the active order's filtered
 * rows onto it. This is the SINGLE matcher shared by all three source-doc
 * consumers — the visual auto-open, the audio auto-play, and the per-order
 * source-row slice gate — so they can never disagree about which attachment IS
 * the source doc (STK-1269).
 */
export function attachmentFilenameMatchesSourceDoc(
  docUrl: string,
  attachmentName: string,
): boolean {
  const rawFilename = docUrl.split("/").pop()?.split(/[?#]/)[0] ?? "";
  const docFilename = rawFilename ? decodedLower(rawFilename) : "";
  // Decode the attachment side too: SMS/WhatsApp names come from the media
  // URL's last segment and may still be percent-encoded, while the doc side
  // is always compared decoded.
  return docFilename !== "" && docFilename === decodedLower(attachmentName);
}

/**
 * Canonical form of a filename for identity comparisons: percent-decoded and
 * lowercased (falling back to the raw segment on malformed encoding). Use this
 * wherever filenames are compared or counted so that encoded and decoded
 * spellings of the same name are treated as one.
 */
export function normalizedFilename(segment: string): string {
  return decodedLower(segment);
}

function decodedLower(segment: string): string {
  try {
    return decodeURIComponent(segment).toLowerCase();
  } catch {
    // Malformed percent-encoding (e.g. a stray "%"): fall back to the raw
    // segment so one bad source URL can't crash the whole recap panel.
    return segment.toLowerCase();
  }
}

/**
 * The MinIO object key a source-document reference points at. `sourceDocuments`
 * entries are either a non-presigned public URL (`{proto}://{host}/{bucket}/{key}`,
 * from `build_public_url`) or a raw storage key (the Delanchy dossier path) — never
 * signed. Strip the scheme+host (URL form) and any query/hash, then percent-decode
 * so it lines up with the stored, decoded attachment key. Returns "" for a
 * non-storage string (e.g. an arbitrary LLM-passed value).
 */
export function sourceDocStorageKey(docUrl: string): string {
  let path = docUrl;
  const scheme = path.indexOf("://");
  if (scheme !== -1) {
    const afterHost = path.slice(scheme + 3).indexOf("/");
    path = afterHost === -1 ? "" : path.slice(scheme + 3 + afterHost + 1);
  }
  path = path.split(/[?#]/)[0];
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
}

/**
 * STRONG-identity match: true when the source-doc reference and the attachment
 * resolve to the SAME storage object by KEY, not just filename. An email
 * attachment's `minio_path`/`url` is its raw object key
 * (`{company}/{user}/email/{uuid}/{file}`); a public-URL source doc carries that
 * same key after the bucket segment, so we match on a `/`-bounded suffix (the
 * per-object uuid makes the suffix unambiguous). This is what distinguishes two
 * attachments that share a display name (STK-1269, greptile). Returns false when
 * either side lacks a path-bearing key — the caller then falls back to the
 * filename match (`attachmentFilenameMatchesSourceDoc`).
 */
export function attachmentKeyMatchesSourceDoc(
  docUrl: string,
  attachment: AttachedFile,
): boolean {
  const attKey = attachment.minio_path || attachment.url || "";
  const docKey = sourceDocStorageKey(docUrl);
  if (!attKey.includes("/") || !docKey.includes("/")) return false;
  return (
    docKey === attKey ||
    docKey.endsWith(`/${attKey}`) ||
    attKey.endsWith(`/${docKey}`)
  );
}

/**
 * Is this attachment the pre-order's source document? Prefers the STRONG storage-
 * key match (disambiguates same-named attachments) and falls back to the strict
 * filename match only when no key identity is available on both sides — exactly
 * "compare the storage path when available, else filename" (STK-1269, greptile).
 */
export function attachmentIsSourceDoc(
  docUrl: string,
  attachment: AttachedFile,
): boolean {
  return (
    attachmentKeyMatchesSourceDoc(docUrl, attachment) ||
    attachmentFilenameMatchesSourceDoc(docUrl, attachment.name)
  );
}
